import { coreBOSMap } from './corebos-map.js';
import { descriptors } from './descriptors/index.js';
/**
 * Build the field metadata catalog for a resource. The coreBOS view arrays reference
 * names beyond the descriptor's own fields — relation columns, audit timestamps, the
 * ownership/numbering columns the behaviors add. We synthesize FieldDefs for those so
 * every view name resolves to metadata (label, type) instead of a bare column name.
 */
/** Italian labels for CRM base targets that are not descriptors in this kit (UI contract rule 11). */
const REF_LABELS = {
    account: 'Azienda', contact: 'Contatto', user: 'Utente', document: 'Documento',
};
function buildFieldCatalog(d) {
    const byName = new Map();
    const put = (f) => {
        if (!byName.has(f.name))
            byName.set(f.name, f);
    };
    for (const f of d.fields)
        put(f);
    for (const r of d.relations ?? []) {
        if (r.kind !== 'belongsTo')
            continue;
        // coreBOS write side references the bare relation name; the kernel read side
        // (descriptorToMigration) names the column `${name}_id`. Catalog both.
        const label = descriptors.find((x) => x.slug === r.target)?.label.singular ?? REF_LABELS[r.target] ?? r.name;
        put({ name: r.name, type: 'ref', refTo: r.target, label });
        put({ name: `${r.name}_id`, type: 'ref', refTo: r.target, label });
    }
    if (d.behaviors?.audit) {
        put({ name: 'createdtime', type: 'datetime', label: 'Creato il' });
        put({ name: 'modifiedtime', type: 'datetime', label: 'Modificato il' });
    }
    if (d.behaviors?.ownership)
        put({ name: 'owner_id', type: 'ref', refTo: 'user', label: 'Assegnato a' });
    if (d.behaviors?.numbering)
        put({ name: d.behaviors.numbering.seq, type: 'text', label: 'Numero' });
    return [...byName.values()];
}
/**
 * Convert a descriptor into a refine resource config. View orderings come from the
 * coreBOS map when the descriptor has an entry there; otherwise they fall back to the
 * descriptor's own field order (all fields for list/show/edit, required for create).
 */
export function descriptorToRefineResource(d, opts) {
    const map = coreBOSMap[d.slug];
    const fieldNames = d.fields.map((f) => f.name);
    const requiredNames = d.fields.filter((f) => f.required).map((f) => f.name);
    return {
        name: d.slug,
        list: `/${d.slug}`,
        show: `/${d.slug}/show/:id`,
        create: `/${d.slug}/create`,
        edit: `/${d.slug}/edit/:id`,
        meta: {
            dataProviderName: opts.dataProviderName,
            label: d.label.plural,
            fields: buildFieldCatalog(d),
            listView: map?.listView ?? fieldNames,
            showView: map?.showView ?? fieldNames,
            editView: map?.editView ?? fieldNames,
            createView: map?.createView ?? { fields: fieldNames, required: requiredNames, defaults: {} },
            searchFields: map?.searchFields ?? requiredNames,
        },
    };
}
/**
 * The AI portal's resource set. Reads route to the kernel projection by default; pass a
 * different provider name to flip the read side (e.g. 'default' to read from coreBOS too).
 */
export function getAiPortalResources(opts = {}) {
    const dataProviderName = opts.dataProviderName ?? 'kernel';
    return descriptors.map((d) => descriptorToRefineResource(d, { dataProviderName }));
}
